import React from 'react';
import { ISentenceScore } from '../api/ScoreApi';

type SentenceScoreDialogContextType = {
    openDialog: (score: ISentenceScore, showCharacters?: boolean) => void;
    closeDialog: () => void;
    isOpen: boolean;
    sentenceScore: ISentenceScore | null;
    learnWithCharacter: boolean;

    isShareOpen: boolean;
    openShareDialog: () => void;
    closeShareDialog: () => void;
};

const SentenceScoreDialogContext = React.createContext<SentenceScoreDialogContextType | undefined>(undefined);

export const SentenceScoreDialogProvider = ({ children }: { children: React.ReactNode }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [sentenceScore, setSentenceScore] = React.useState<ISentenceScore | null>(null);
    const [learnWithCharacter, setLearnWithCharacter] = React.useState(false);

    const [isShareOpen, setIsShareOpen] = React.useState(false);

    const openDialog = React.useCallback((score: ISentenceScore, showCharacters: boolean = true) => {
        setSentenceScore(score);
        setLearnWithCharacter(showCharacters);
        setIsOpen(true);
    }, []);

    const closeDialog = React.useCallback(() => {
        setIsOpen(false);
        setSentenceScore(null);
        setIsShareOpen(false);
    }, []);

    const openShareDialog = React.useCallback(() => setIsShareOpen(true), []);
    const closeShareDialog = React.useCallback(() => setIsShareOpen(false), []);

    return (
        <SentenceScoreDialogContext.Provider
            value={{
                openDialog,
                closeDialog,
                isOpen,
                sentenceScore,
                learnWithCharacter,
                isShareOpen,
                openShareDialog,
                closeShareDialog
            }}
        >
            {children}
        </SentenceScoreDialogContext.Provider>
    );
};

export const useSentenceScoreDialog = () => {
    const context = React.useContext(SentenceScoreDialogContext);
    if (!context) throw new Error("useSentenceScoreDialog must be used within SentenceScoreDialogProvider");
    return context;
};